import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import {
  Check, ArrowRight, ArrowLeft, Bot, UserCheck, Star, CalendarDays, Clock, Timer,
  CheckCircle2, Loader2, Video, Phone, Sparkles,
} from "lucide-react";

const AI_STEPS = ["Job Role", "Interview Type", "AI Mode", "Panel", "Schedule", "Confirm"];
const EXPERT_STEPS = ["Job Role", "Interview Type", "Panel", "Schedule", "Confirm"];

const Card = ({ children, className = "", active, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-left rounded-2xl border p-5 transition-all bg-white ${active ? "border-brand ring-2 ring-brand/30 shadow-ambient" : "border-slate-200 hover:border-slate-300 hover:shadow-card"} ${className}`}>
    {children}
  </button>
);

const modeIcon = (m) => (m === "voice" ? "📞" : m === "human" ? "👤" : "🎥");
const phoneValid = (p) => (p || "").replace(/\D/g, "").length >= 8;

export default function BookInterview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const presetMode = params.get("mode"); // video | voice | human

  const [step, setStep] = useState(0);
  const [roles, setRoles] = useState([]);
  const [types, setTypes] = useState([]);
  const [aiModes, setAiModes] = useState([]);
  const [panels, setPanels] = useState([]);
  const [slots, setSlots] = useState({ dates: [], slots: [], durations: [] });
  const [sel, setSel] = useState({
    role: null,
    type: presetMode ? (presetMode === "human" ? "expert" : "ai") : null,
    mode: presetMode && presetMode !== "human" ? presetMode : null,
    panel: null, date: null, time: null, duration: 45,
    phone: user?.phone || "",
  });
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    api.get("/catalog/job-roles").then((r) => setRoles(r.data));
    api.get("/catalog/interview-types").then((r) => setTypes(r.data));
    api.get("/catalog/ai-modes").then((r) => setAiModes(r.data));
    api.get("/catalog/slots").then((r) => setSlots(r.data));
  }, []);

  useEffect(() => {
    if (sel.type) api.get(`/catalog/panels?type=${sel.type}`).then((r) => setPanels(sel.type === "ai" ? r.data.panels : r.data.experts));
  }, [sel.type]);

  const STEPS = sel.type === "expert" ? EXPERT_STEPS : AI_STEPS;
  const key = STEPS[step];
  const isVoice = sel.type === "ai" && sel.mode === "voice";

  const canNextByKey = {
    "Job Role": !!sel.role,
    "Interview Type": !!sel.type,
    "AI Mode": !!sel.mode && (sel.mode !== "voice" || phoneValid(sel.phone)),
    "Panel": !!sel.panel,
    "Schedule": !!(sel.date && sel.time),
    "Confirm": true,
  };
  const canNext = canNextByKey[key];

  const categories = [...new Set(roles.map((r) => r.category))];

  const confirm = async () => {
    setBooking(true);
    try {
      const { data } = await api.post("/interviews/book", {
        role_id: sel.role.id, type: sel.type,
        mode: sel.type === "ai" ? sel.mode : undefined,
        panel_id: sel.panel.id, date: sel.date, time: sel.time, duration: sel.duration,
        phone: isVoice ? sel.phone : undefined,
      });
      setBooked(data);
      toast.success("Interview successfully booked!");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(formatError(e.response?.data?.detail));
        navigate("/candidate/billing");
        return;
      }
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBooking(false);
    }
  };

  if (booked) {
    return (
      <div className="max-w-lg mx-auto fade-up">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-8 text-center">
          <div className="h-16 w-16 rounded-2xl bg-emerald-100 grid place-items-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-ink">Interview Successfully Booked</h1>
          <p className="text-ink-soft text-sm mt-1">{booked.mode === "voice" ? "Mock2Crack will call you at your scheduled time." : "Your interview is scheduled. Attend it whenever you're ready."}</p>
          <div className="mt-6 text-left rounded-xl border border-slate-200 divide-y divide-slate-100">
            {[
              ["Job Role", booked.role_name],
              ["Interview Type", booked.type === "ai" ? "AI Interviews" : "Human Expert Interview"],
              ["Mode", `${booked.mode_icon || modeIcon(booked.mode)} ${booked.mode_label}`],
              ["Panel", booked.panel_name], ["Date", booked.date], ["Time", booked.time], ["Duration", `${booked.duration} min`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between px-4 py-2.5 text-sm">
                <span className="text-ink-faint">{k}</span><span className="font-semibold text-ink">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-3">
            <button data-testid="attend-now" onClick={() => navigate(`/candidate/interview/${booked.id}`)}
              className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full shadow-ambient transition-colors">
              {booked.mode === "voice" ? "View Interview Details" : "Attend Interview"} <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={() => navigate("/candidate")} className="text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-5 py-3 rounded-full transition-colors">
              Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Book an Interview</h1>
      {/* Stepper */}
      <div className="flex items-center gap-2 mt-5 mb-8 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${i === step ? "bg-brand text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-ink-faint"}`}>
              {i < step ? <Check className="h-3.5 w-3.5" /> : <span className="num">{i + 1}</span>} {s}
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-4 bg-slate-200" />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="fade-up" key={key}>
        {key === "Job Role" && (
          <div className="space-y-6">
            {categories.map((cat) => (
              <div key={cat}>
                <div className="text-xs font-bold uppercase tracking-wider text-ink-faint mb-3">{cat}</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {roles.filter((r) => r.category === cat).map((r) => (
                    <Card key={r.id} testid={`role-${r.id}`} active={sel.role?.id === r.id} onClick={() => setSel({ ...sel, role: r })}>
                      <div className="flex items-start justify-between">
                        <div className="font-heading font-bold text-ink">{r.name}</div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.difficulty === "Hard" ? "bg-red-100 text-red-700" : r.difficulty === "Medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.difficulty}</span>
                      </div>
                      <p className="text-xs text-ink-soft mt-1 leading-relaxed">{r.description}</p>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {r.required_skills.slice(0, 4).map((s) => <span key={s} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-ink-soft">{s}</span>)}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {key === "Interview Type" && (
          <div className="grid sm:grid-cols-2 gap-4">
            {types.map((t) => (
              <Card key={t.id} testid={`type-${t.id}`} active={sel.type === t.id}
                onClick={() => setSel({ ...sel, type: t.id, mode: t.id === "ai" ? sel.mode : null, panel: null })}>
                <span className={`h-11 w-11 rounded-xl grid place-items-center mb-4 ${t.id === "ai" ? "bg-brand-light" : "bg-slate-900"}`}>
                  {t.id === "ai" ? <Bot className="h-5 w-5 text-brand" /> : <UserCheck className="h-5 w-5 text-white" />}
                </span>
                <div className="font-heading text-lg font-bold text-ink">{t.name}</div>
                <div className="text-xs text-brand font-semibold num">{t.credits} credit{t.credits > 1 ? "s" : ""}</div>
                <p className="text-sm text-ink-soft mt-1">{t.tagline}</p>
                <ul className="mt-3 space-y-1.5">
                  {t.features.map((f) => <li key={f} className="flex items-center gap-2 text-xs text-ink-soft"><Check className="h-3.5 w-3.5 text-emerald-500" /> {f}</li>)}
                </ul>
              </Card>
            ))}
          </div>
        )}

        {key === "AI Mode" && (
          <div>
            <div className="text-center max-w-xl mx-auto mb-6">
              <h2 className="font-heading text-2xl font-bold text-ink">Choose Your AI Interview</h2>
              <p className="text-ink-soft text-sm mt-1">Choose how you want to practice your interview.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {aiModes.map((m) => (
                <Card key={m.id} testid={`aimode-${m.id}`} active={sel.mode === m.id}
                  onClick={() => setSel({ ...sel, mode: m.id, panel: null })}>
                  <div className="flex items-center justify-between mb-4">
                    <span className={`h-12 w-12 rounded-xl grid place-items-center ${m.id === "video" ? "bg-brand-light" : "bg-emerald-100"}`}>
                      {m.id === "video" ? <Video className="h-6 w-6 text-brand" /> : <Phone className="h-6 w-6 text-emerald-600" />}
                    </span>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${m.id === "video" ? "bg-brand-light text-brand" : "bg-emerald-100 text-emerald-700"}`}>{m.badge}</span>
                  </div>
                  <div className="font-heading text-lg font-bold text-ink">{modeIcon(m.id)} {m.name}</div>
                  <p className="text-sm text-ink-soft mt-1">{m.short}</p>
                  <ul className="mt-3 space-y-1.5">
                    {m.card_features.map((f) => <li key={f} className="flex items-center gap-2 text-xs text-ink-soft"><Check className="h-3.5 w-3.5 text-emerald-500" /> {f}</li>)}
                  </ul>
                  <div className={`mt-4 text-center text-sm font-bold px-4 py-2.5 rounded-full ${sel.mode === m.id ? "bg-brand text-white" : "bg-slate-100 text-ink"}`}>
                    {m.id === "video" ? "Select Video Interview" : "Select Voice Interview"}
                  </div>
                </Card>
              ))}
            </div>
            {isVoice && (
              <div className="mt-6 max-w-md rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2"><Phone className="h-4 w-4" /> Confirm your phone number</div>
                <p className="text-xs text-ink-soft mb-3">Mock2Crack's AI interviewer will call this number at your scheduled time.</p>
                <input data-testid="voice-phone-input" value={sel.phone} onChange={(e) => setSel({ ...sel, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm num focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none" />
                {!phoneValid(sel.phone) && sel.phone.length > 0 && <p className="text-xs text-red-500 mt-1.5">Enter a valid phone number.</p>}
              </div>
            )}
          </div>
        )}

        {key === "Panel" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {panels.map((p) => (
              <Card key={p.id} testid={`panel-${p.id}`} active={sel.panel?.id === p.id} onClick={() => setSel({ ...sel, panel: p })}>
                {sel.type === "ai" ? (
                  <div className="flex items-center gap-3">
                    <span className="h-11 w-11 rounded-full bg-brand-light grid place-items-center num font-bold text-brand text-sm">{p.avatar}</span>
                    <div><div className="font-semibold text-ink text-sm">{p.name}</div><div className="text-xs text-ink-faint">{p.focus}</div></div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt={p.name} className="h-12 w-12 rounded-full object-cover" />
                      <div><div className="font-semibold text-ink text-sm">{p.name}</div><div className="text-xs text-ink-faint">{p.designation}</div></div>
                    </div>
                    <div className="mt-3 text-xs text-ink-soft">{p.company} · {p.experience}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex flex-wrap gap-1">{p.expertise.slice(0, 2).map((e) => <span key={e} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100">{e}</span>)}</div>
                      <span className="flex items-center gap-1 text-xs font-bold text-amber-500"><Star className="h-3.5 w-3.5 fill-amber-400" /> {p.rating}</span>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {key === "Schedule" && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-faint mb-3"><CalendarDays className="h-4 w-4" /> Select Date</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
                {slots.dates.map((d) => {
                  const dt = new Date(d);
                  return (
                    <button key={d} data-testid={`date-${d}`} onClick={() => setSel({ ...sel, date: d })}
                      className={`rounded-xl border p-2.5 text-center transition-colors ${sel.date === d ? "border-brand bg-brand text-white" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="text-[10px] font-semibold uppercase">{dt.toLocaleDateString("en", { weekday: "short" })}</div>
                      <div className="num font-bold text-lg">{dt.getDate()}</div>
                      <div className="text-[10px]">{dt.toLocaleDateString("en", { month: "short" })}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-faint mb-3"><Clock className="h-4 w-4" /> Select Time</div>
              <div className="flex flex-wrap gap-2">
                {slots.slots.map((t) => (
                  <button key={t} data-testid={`time-${t.replace(/[:\s]/g, "")}`} onClick={() => setSel({ ...sel, time: t })}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors num ${sel.time === t ? "border-brand bg-brand text-white" : "border-slate-200 hover:border-slate-300 text-ink-soft"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-faint mb-3"><Timer className="h-4 w-4" /> Duration</div>
              <div className="flex gap-2">
                {slots.durations.map((d) => (
                  <button key={d} onClick={() => setSel({ ...sel, duration: d })}
                    className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors num ${sel.duration === d ? "border-brand bg-brand text-white" : "border-slate-200 hover:border-slate-300 text-ink-soft"}`}>{d} min</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {key === "Confirm" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6 max-w-lg">
            <div className="font-heading font-bold text-ink mb-4">Review your booking</div>
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
              {[
                ["Job Role", sel.role?.name],
                ["Interview Type", sel.type === "ai" ? "AI Interviews" : "Human Expert Interview"],
                ["Mode", `${modeIcon(sel.type === "ai" ? sel.mode : "human")} ${sel.type === "ai" ? (sel.mode === "voice" ? "AI Voice Interview" : "AI Video Interview") : "Human Expert Interview"}`],
                ...(isVoice ? [["Phone", sel.phone]] : []),
                ["Panel", sel.panel?.name], ["Date", sel.date], ["Time", sel.time], ["Duration", `${sel.duration} min`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-2.5 text-sm"><span className="text-ink-faint">{k}</span><span className="font-semibold text-ink">{v}</span></div>
              ))}
            </div>
            <button data-testid="confirm-booking" onClick={confirm} disabled={booking}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3.5 rounded-full shadow-ambient disabled:opacity-60 transition-colors">
              {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Confirm & Book <Check className="h-4 w-4" /></>}
            </button>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-8">
        <button data-testid="step-back" onClick={() => step === 0 ? navigate("/candidate") : setStep(step - 1)}
          className="inline-flex items-center gap-2 text-sm font-bold text-ink-soft hover:text-ink px-4 py-2.5 rounded-full transition-colors">
          <ArrowLeft className="h-4 w-4" /> {step === 0 ? "Cancel" : "Back"}
        </button>
        {step < STEPS.length - 1 && (
          <button data-testid="step-next" disabled={!canNext} onClick={() => setStep(step + 1)}
            className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-6 py-2.5 rounded-full disabled:opacity-40 transition-colors">
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
